
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { TypographyConfiguration } from "@/types/block-configurations";
import { ColorPicker } from "./ColorPicker";

interface TypographyControlsProps {
  value: TypographyConfiguration;
  onChange: (value: TypographyConfiguration) => void;
}

const FONT_FAMILIES = [
  'Arial',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
  'Courier New'
] as const;

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: '300', label: 'Light' },
  { value: '600', label: 'Semi Bold' },
] as const;

/**
 * TypographyControls Component
 * 
 * Provides controls for text styling including:
 * - Font family (email-safe fonts only)
 * - Font size (8-72px)
 * - Font weight
 * - Line height (1.0-3.0)
 * - Text color
 * 
 * @param value - Current typography configuration
 * @param onChange - Callback when any property changes
 */
export function TypographyControls({ value, onChange }: TypographyControlsProps) {
  const updateField = <K extends keyof TypographyConfiguration>(
    field: K,
    newValue: TypographyConfiguration[K]
  ) => {
    onChange({ ...value, [field]: newValue });
  };

  return (
    <div className="space-y-2">
      {/* Row 1: Font Family + Weight + Size */}
      <div className="grid grid-cols-[1fr_1fr_52px] gap-1.5 items-end">
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Font</Label>
          <Select
            value={value.fontFamily}
            onChange={(e) => updateField('fontFamily', e.target.value as TypographyConfiguration['fontFamily'])}
            className="h-7 text-xs"
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Weight</Label>
          <Select
            value={value.fontWeight}
            onChange={(e) => updateField('fontWeight', e.target.value as TypographyConfiguration['fontWeight'])}
            className="h-7 text-xs"
          >
            {FONT_WEIGHTS.map(({ value: weightValue, label }) => (
              <option key={weightValue} value={weightValue}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Size</Label>
          <Input
            type="number"
            min={8}
            max={72}
            step={1}
            value={value.fontSize}
            onChange={(e) => updateField('fontSize', parseInt(e.target.value) || 14)}
            className="h-7 text-xs px-1.5 text-center"
          />
        </div>
      </div>

      {/* Row 2: Color + Line Height */}
      <div className="grid grid-cols-[1fr_60px] gap-1.5 items-end">
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Color</Label>
          <ColorPicker
            value={value.color}
            onChange={(color) => updateField('color', color ?? '')}
            className="h-7"
          />
        </div>
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Line H.</Label>
          <Input
            type="number"
            min={1.0}
            max={3.0}
            step={0.1}
            value={value.lineHeight}
            onChange={(e) => updateField('lineHeight', parseFloat(parseFloat(e.target.value).toFixed(1)) || 1.5)}
            className="h-7 text-xs px-1.5 text-center"
          />
        </div>
      </div>

      {/* Row 3: Style toggles (I / U / S) */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => updateField('italic', !value.italic)}
          className={`flex-1 rounded border px-2 py-0.5 text-xs font-medium transition-colors ${
            value.italic
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-foreground hover:bg-accent'
          }`}
          title="Italic"
        >
          <span style={{ fontStyle: 'italic' }}>I</span>
        </button>
        <button
          type="button"
          onClick={() => updateField('underline', !value.underline)}
          className={`flex-1 rounded border px-2 py-0.5 text-xs font-medium transition-colors ${
            value.underline
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-foreground hover:bg-accent'
          }`}
          title="Underline"
        >
          <span style={{ textDecoration: 'underline' }}>U</span>
        </button>
        <button
          type="button"
          onClick={() => updateField('strikethrough', !value.strikethrough)}
          className={`flex-1 rounded border px-2 py-0.5 text-xs font-medium transition-colors ${
            value.strikethrough
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-foreground hover:bg-accent'
          }`}
          title="Strikethrough"
        >
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </button>
      </div>

      {/* Row 4: Transform + Letter Spacing */}
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Transform</Label>
          <Select
            value={value.textTransform}
            onChange={(e) => updateField('textTransform', e.target.value as TypographyConfiguration['textTransform'])}
            className="h-7 text-xs"
          >
            <option value="none">None</option>
            <option value="uppercase">UPPERCASE</option>
            <option value="lowercase">lowercase</option>
            <option value="capitalize">Capitalize</option>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-medium mb-0.5 block">Tracking</Label>
          <Input
            type="number"
            min={0}
            max={10}
            step={0.5}
            value={value.letterSpacing}
            onChange={(e) => updateField('letterSpacing', parseFloat(e.target.value) || 0)}
            className="h-7 text-xs px-1.5 text-center"
            title="Letter spacing in pixels"
          />
        </div>
      </div>
    </div>
  );
}
